import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, makeStyles, Tooltip } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import { FaGithub } from 'react-icons/fa';
import { SiSonarqube, SiJira, SiN8N, SiRailway } from 'react-icons/si';
import { FaSlack } from 'react-icons/fa6';
import { IoLogoDocker } from 'react-icons/io5';
import { MdOutlineAnnouncement } from 'react-icons/md';
import { FaTools, FaBook } from 'react-icons/fa';

type Tool = {
  url: string;
  label: string;
  subtitle: string;
  icon: ReactNode;
  color: string;
  external?: boolean;
};

const TOOLS: Tool[] = [
  {
    url: '/docs',
    label: 'Docs',
    subtitle: 'TechDocs',
    icon: <FaBook size={20} />,
    color: '#22c55e',
  },
  {
    url: 'https://github.com/fluxem-sas',
    label: 'Repository',
    subtitle: 'GitHub Org',
    icon: <FaGithub size={20} />,
    color: '#a78bfa',
    external: true,
  },
  {
    url: 'https://sonarqube.fluxem.co',
    label: 'Sonarqube',
    subtitle: 'Calidad',
    icon: <SiSonarqube size={20} />,
    color: '#38bdf8',
    external: true,
  },
  {
    url: 'https://fluxem.slack.com',
    label: 'Slack',
    subtitle: '#eng-core',
    icon: <FaSlack size={20} />,
    color: '#facc15',
    external: true,
  },
  {
    url: 'https://n8n.fluxem.co',
    label: 'N8N',
    subtitle: 'Pipelines',
    icon: <SiN8N size={20} />,
    color: '#f87171',
    external: true,
  },
  {
    url: 'https://hub.docker.com/repositories/fluxem-sas',
    label: 'Dockerhub',
    subtitle: 'Registry',
    icon: <IoLogoDocker size={20} />,
    color: '#60a5fa',
    external: true,
  },
  {
    url: 'https://fluxem.atlassian.net/jira',
    label: 'Jira',
    subtitle: 'Sprint',
    icon: <SiJira size={20} />,
    color: '#818cf8',
    external: true,
  },
  {
    url: 'https://railway.com',
    label: 'Railway',
    subtitle: 'Cloud',
    icon: <SiRailway size={20} />,
    color: '#c084fc',
    external: true,
  },
  {
    url: '/announcements',
    label: 'Avisos',
    subtitle: 'Status',
    icon: <MdOutlineAnnouncement size={20} />,
    color: '#fb923c',
  },
  {
    url: '/devtools',
    label: 'DevTools',
    subtitle: 'CLI & SDKs',
    icon: <FaTools size={18} />,
    color: '#94a3b8',
  },
];

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap' as const,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: 'var(--content-primary, ' + theme.palette.text.primary + ')',
  },
  badge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid var(--color-divider, ' + theme.palette.divider + ')',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    backgroundColor: 'var(--surface-paper, ' + theme.palette.background.paper + ')',
  },
  subtitle: {
    fontSize: '0.8125rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    marginTop: 2,
  },
  customize: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.8125rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    textDecoration: 'none',
    '&:hover': {
      color: 'var(--interactive-primary, ' + theme.palette.primary.main + ')',
    },
  },
  tray: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: '1px solid var(--color-divider, ' + theme.palette.divider + ')',
    backgroundColor:
      'var(--surface-paper, ' + theme.palette.background.paper + ')',
  },
  tool: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    textDecoration: 'none',
    color: 'inherit',
    padding: theme.spacing(1, 0.5),
    borderRadius: 10,
    transition: 'background-color 0.15s ease, transform 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--action-hover, rgba(145,158,171,0.08))',
      transform: 'translateY(-1px)',
    },
  },
  iconWrap: {
    position: 'relative' as const,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    border: '1px solid',
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  statusDot: {
    position: 'absolute' as const,
    top: -2,
    right: -2,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    boxShadow: '0 0 0 2px var(--surface-paper, #171f27)',
  },
  toolLabel: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'var(--content-primary, ' + theme.palette.text.primary + ')',
    lineHeight: 1.2,
  },
  toolSub: {
    fontSize: '0.6875rem',
    color: 'var(--content-secondary, ' + theme.palette.text.secondary + ')',
    marginTop: 2,
  },
}));

export function ToolkitSection() {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <div className={classes.header}>
        <div>
          <div className={classes.titleRow}>
            <Typography className={classes.title}>Toolkit</Typography>
            <span className={classes.badge}>{TOOLS.length} Accesos rápidos</span>
          </div>
          <Typography className={classes.subtitle}>
            Herramientas esenciales del desarrollador e infraestructura de Fluxem
          </Typography>
        </div>
        <Tooltip title="Configuración del portal">
          <RouterLink to="/settings" className={classes.customize}>
            <SettingsIcon style={{ fontSize: 16 }} />
            Personalizar accesos
          </RouterLink>
        </Tooltip>
      </div>

      <Paper className={classes.tray} elevation={0}>
        {TOOLS.map(tool => {
          const content = (
            <>
              <div
                className={classes.iconWrap}
                style={{
                  borderColor: `${tool.color}55`,
                  color: tool.color,
                  boxShadow: `inset 0 0 0 1px ${tool.color}22`,
                }}
              >
                {tool.icon}
                <span className={classes.statusDot} />
              </div>
              <span className={classes.toolLabel}>{tool.label}</span>
              <span className={classes.toolSub}>{tool.subtitle}</span>
            </>
          );

          if (tool.external) {
            return (
              <a
                key={tool.label}
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.tool}
              >
                {content}
              </a>
            );
          }

          return (
            <RouterLink key={tool.label} to={tool.url} className={classes.tool}>
              {content}
            </RouterLink>
          );
        })}
      </Paper>
    </Box>
  );
}
